import { createForm } from "@tanstack/solid-form";
import { createMemo, createSignal, Show } from "solid-js";
import { produce } from "solid-js/store";
import { toast } from "solid-sonner";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { DialogImageUploader } from "@/components/shared/dialog-image-uploader";
import { LocationPopoverForm } from "@/components/shared/geo-code/location-popover";
import { PhoneInput, phoneNumberSchema } from "@/components/shared/input";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  FieldSectionTitle,
  Input,
} from "@/components/ui/input";
import { useCheckEmailExists } from "@/domains/auth/api";
import { UPLOAD_RULES } from "@/domains/documents/constants/upload";
import { getUploadApiError } from "@/domains/documents/utils/upload-error";
import { useUpdateProfileImage } from "@/domains/freelance/profile/api";
import { optionUrlCustomSchema } from "@/libs/zod-errors";
import profile_picture from "../../assets/profile-pic.png";
import {
  linkedinOnboardingValidationSchema,
  useOnboardingStore,
} from "../../context/OnboardingContext";
import { defaultBirthdateView, maxAdultBirthdate } from "../../utils/birthdate";

// Extract personal info validation schema from the main schema
const personalValidationSchema =
  linkedinOnboardingValidationSchema.shape.info.pick({
    residency: true,
    date_of_birth: true,
  });

// Extended schema with optional phone_number and email (when temporary)
const extendedPersonalSchema = personalValidationSchema.extend({
  phone_number: phoneNumberSchema.optional(),
  email: z.string().email().optional(),
  website: optionUrlCustomSchema,
  portfolio: optionUrlCustomSchema,
});

type PersonalFormValues = z.infer<typeof extendedPersonalSchema>;

interface PersonalModalProps {
  onClose: () => void;
}

export const PersonalModal = (props: PersonalModalProps) => {
  const [store, setStore] = useOnboardingStore();
  const { t } = useI18n();
  const checkEmailExists = useCheckEmailExists();

  // Stable 18+ boundary so the date picker uses one maxDate for the whole calendar render
  const maxBirthdate = createMemo(() => maxAdultBirthdate());
  const birthdateView = createMemo(() => defaultBirthdateView());

  // Verifica se l'email iniziale era temporanea (una volta sola, non reattiva)
  const initialEmail = store.profile.email;
  const [shouldShowEmailField] = createSignal(
    initialEmail?.includes("@sevedemo.invite") ?? false,
  );

  const form = createForm(() => ({
    defaultValues: {
      date_of_birth: store.info.value.date_of_birth
        ? new Date(store.info.value.date_of_birth)
        : undefined,
      residency: store.info.value.residency ?? "",
      phone_number: store.info.value.phone_number ?? "",
      email: shouldShowEmailField() ? "" : undefined,
      website: store.online_presence.value.website ?? "",
      portfolio: store.online_presence.value.portfolio ?? "",
    } as PersonalFormValues,
    onSubmit: async ({ value }) => {
      const validatedData = extendedPersonalSchema.safeParse(value);
      if (!validatedData.success) {
        console.error("Validation error", validatedData.error);
        return;
      }

      // Update store with validated data
      setStore(
        produce((draft) => {
          draft.info.value.date_of_birth = validatedData.data.date_of_birth;
          draft.info.value.residency = validatedData.data.residency;
          draft.info.value.phone_number = validatedData.data.phone_number;
          draft.online_presence.value.website = validatedData.data.website;
          draft.online_presence.value.portfolio = validatedData.data.portfolio;
          // Se l'email iniziale era temporanea e viene fornita una nuova email, aggiorna il profilo
          if (shouldShowEmailField() && validatedData.data.email) {
            draft.profile.email = validatedData.data.email;
          }
          draft.info.error = null;
        }),
      );

      props.onClose();
    },
  }));

  const updateProfileImage = useUpdateProfileImage();

  const handleUpload = async (image: File) => {
    toast.loading(t("common.image_upload.loading"), { id: "onboarding-image" });
    try {
      const response = await updateProfileImage.mutateAsync({ image });
      toast.success(t("common.image_upload.success"), {
        id: "onboarding-image",
      });
      setStore(
        produce((draft) => {
          draft.info.value.avatar_url = response.profile.avatar_url;
        }),
      );
    } catch (error) {
      // Upload errors (413/415) are shown inline by the dialog (D10); dismiss the
      // loading toast and rethrow so the dialog can map them. Others keep the toast.
      if (getUploadApiError(error)) {
        toast.dismiss("onboarding-image");
      } else {
        toast.error(t("common.image_upload.error"), { id: "onboarding-image" });
      }
      throw error;
    }
  };

  return (
    <div class="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>{t("onboarding.steps.info.title")}</DialogTitle>
        <DialogDescription>
          {t("onboarding.steps.info.description")}
        </DialogDescription>
      </DialogHeader>

      <div class="flex flex-col md:flex-row gap-4 justify-between items-start">
        <div class="w-1/4">
          <DialogImageUploader
            onUpload={handleUpload}
            uploadRules={UPLOAD_RULES.profileImage}
          >
            <div
              class="w-24 h-24 md:w-48 md:h-48 rounded-full flex items-center justify-center relative hover:opacity-80 transition-opacity duration-300 overflow-hidden"
              classList={{
                "bg-gray-200": !store.info.value.avatar_url,
              }}
            >
              <Show
                when={store.info.value.avatar_url}
                fallback={<img src={profile_picture} alt="User Profile" />}
              >
                <img src={store.info.value.avatar_url!} alt="User Profile" />
              </Show>
            </div>
            <Show
              when={!store.info.value.avatar_url}
              fallback={
                <span class="text-sm">{t("common.image_upload.edit")}</span>
              }
            >
              <span class="text-sm">{t("common.image_upload.upload")}</span>
            </Show>
          </DialogImageUploader>
        </div>
        <div class="flex flex-col gap-8 w-full md:w-3/4">
          <form class="space-y-4">
            <div class="border-b border-neutral-200 pb-4 w-full space-y-4">
              <FieldSectionTitle class="text-md font-medium">
                Informazioni personali
              </FieldSectionTitle>

              <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <form.Field
                  name="date_of_birth"
                  validators={{
                    onChange: extendedPersonalSchema.shape.date_of_birth,
                  }}
                >
                  {(field) => (
                    <FieldContainer class="w-fit">
                      <FieldContainerLabel for="date_of_birth">
                        {t("onboarding.steps.info.dateOfBirth")}
                      </FieldContainerLabel>
                      <DatePicker
                        id={field().name}
                        value={field().state.value}
                        onChange={(date) => field().handleChange(date)}
                        placeholder={t("onboarding.steps.info.dateOfBirthHelp")}
                        maxDate={maxBirthdate()}
                        defaultViewDate={birthdateView()}
                      />
                      <FieldInfo
                        field={field()}
                        helpText={t("onboarding.steps.info.dateOfBirthHelp")}
                      />
                    </FieldContainer>
                  )}
                </form.Field>

                <form.Field
                  name="phone_number"
                  validators={{
                    onChange: extendedPersonalSchema.shape.phone_number,
                  }}
                >
                  {(field) => (
                    <FieldContainer>
                      <FieldContainerLabel for="phone_number">
                        {t("onboarding.steps.info.phoneNumber")}
                        <span class="text-xs text-gray-500 italic ml-1">
                          {" "}
                          (opzionale)
                        </span>
                      </FieldContainerLabel>
                      <PhoneInput fieldApi={field()} />
                    </FieldContainer>
                  )}
                </form.Field>
              </div>

              <Show when={shouldShowEmailField()}>
                <form.Field
                  name="email"
                  validators={{
                    onChange: ({ value }) => {
                      // Validazione sincrona per formato email
                      if (!value || value.trim() === "") {
                        return "Email richiesta per completare l'onboarding";
                      }

                      // Verifica formato email
                      const emailResult = z.string().email().safeParse(value);
                      if (!emailResult.success) {
                        return "Inserisci un'email valida";
                      }

                      return undefined;
                    },
                    onBlurAsync: async ({ value }) => {
                      // Validazione asincrona per verificare se l'email esiste già
                      if (!value || value.trim() === "") {
                        return "Email richiesta per completare l'onboarding";
                      }

                      // Verifica formato email
                      const emailResult = z.string().email().safeParse(value);
                      if (!emailResult.success) {
                        return "Inserisci un'email valida";
                      }

                      // Verifica se l'email esiste già (solo se è valida)
                      try {
                        const emailCheck = await checkEmailExists.mutateAsync(
                          value.trim(),
                        );
                        if (emailCheck.exists) {
                          return "Questa email è già registrata su SeVedemo";
                        }
                      } catch (error) {
                        // Se c'è un errore nella verifica, non blocchiamo l'utente
                        // ma loggiamo l'errore
                        console.error("Errore nella verifica email:", error);
                        return "Errore nella verifica dell'email. Riprova più tardi.";
                      }

                      return undefined;
                    },
                  }}
                >
                  {(field) => (
                    <FieldContainer>
                      <FieldContainerLabel for="email">
                        Email *
                      </FieldContainerLabel>
                      <Input
                        id={field().name}
                        name={field().name}
                        type="email"
                        value={field().state.value ?? initialEmail ?? ""}
                        onInput={(e) => field().handleChange(e.target.value)}
                        onBlur={() => field().handleBlur()}
                        placeholder="nome@esempio.com"
                        classList={{
                          "border-red-500":
                            field().state.meta.isTouched &&
                            field().state.meta.errors.length > 0,
                        }}
                      />
                      <FieldInfo field={field()} />
                      <p class="text-xs text-gray-500 mt-1">
                        Questa email verrà utilizzata per accedere al tuo
                        account
                      </p>
                    </FieldContainer>
                  )}
                </form.Field>
              </Show>
              <form.Field
                name="residency"
                validators={{
                  onChange: extendedPersonalSchema.shape.residency,
                }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for="residency">
                      {t("onboarding.steps.info.residency")}
                    </FieldContainerLabel>
                    <LocationPopoverForm fieldsApi={field()} />
                    <FieldInfo
                      field={field()}
                      helpText={t("onboarding.steps.info.residencyHelp")}
                    />
                  </FieldContainer>
                )}
              </form.Field>
            </div>
            <div>
              <div class="w-full space-y-4">
                <FieldSectionTitle class="text-md font-medium">
                  Presenza online
                </FieldSectionTitle>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <form.Field
                    name="website"
                    validators={{
                      onChange: extendedPersonalSchema.shape.website,
                    }}
                  >
                    {(field) => (
                      <FieldContainer>
                        <FieldContainerLabel for="website">
                          {t("onboarding.steps.info.website")}
                          <span class="text-xs text-gray-500 italic ml-1">
                            {" "}
                            (opzionale)
                          </span>
                        </FieldContainerLabel>
                        <Input
                          id={field().name}
                          name={field().name}
                          type="url"
                          value={field().state.value ?? ""}
                          onInput={(e) => field().handleChange(e.target.value)}
                          onBlur={() => field().handleBlur()}
                          onChange={(e) => field().handleChange(e.target.value)}
                          placeholder="https://yourwebsite.com"
                          class="w-full"
                        />
                        <FieldInfo field={field()} />
                      </FieldContainer>
                    )}
                  </form.Field>
                  <form.Field
                    name="portfolio"
                    validators={{
                      onChange: extendedPersonalSchema.shape.portfolio,
                    }}
                  >
                    {(field) => (
                      <FieldContainer>
                        <FieldContainerLabel for="portfolio">
                          {t("onboarding.steps.info.portfolio")}
                          <span class="text-xs text-gray-500 italic ml-1">
                            {" "}
                            (opzionale)
                          </span>
                        </FieldContainerLabel>
                        <Input
                          id={field().name}
                          name={field().name}
                          type="url"
                          value={field().state.value ?? ""}
                          onInput={(e) => field().handleChange(e.target.value)}
                          onBlur={() => field().handleBlur()}
                          onChange={(e) => field().handleChange(e.target.value)}
                          placeholder="https://yourportfolio.com"
                          class="w-full"
                        />
                        <FieldInfo field={field()} />
                      </FieldContainer>
                    )}
                  </form.Field>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div class="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={props.onClose}>
          {t("common.cancel")}
        </Button>
        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
            isValid: state.isValid,
            isSubmittable: extendedPersonalSchema.safeParse(state.values)
              .success,
          })}
        >
          {(state) => (
            <Button
              onClick={form.handleSubmit}
              disabled={
                !state().isValid ||
                state().isSubmitting ||
                !state().isSubmittable
              }
            >
              {state().isSubmitting ? t("common.saving") : t("common.save")}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </div>
  );
};
